/**
 * nativeShare — partage natif MAISON, indépendant de la couche Median.
 *
 * Pourquoi ce service existe. Le partage passait par l'API de Median, qui
 * exige `window.html2canvas` en variable globale et qui, lorsqu'elle manque,
 * ne fait rien : pas d'exception, pas de promesse rejetée, juste `undefined`.
 * Côté écran, on affichait donc « Partagé ! » alors qu'aucune feuille de
 * partage ne s'était ouverte. C'était vrai sur les deux plateformes.
 *
 * Le partage est découpé là où chaque camp est bon : le web produit l'image
 * (il sait déjà rendre un canvas), le natif ouvre la feuille de partage du
 * système. Le natif ne fait qu'une chose, mais il la fait vraiment, et il
 * répond — succès comme échec.
 *
 * Ponts, tous deux écrits par nos soins :
 *   - Android : `window.kokenShareAndroid.postMessage(json)`
 *     (com.koken.share.KokenShare, addJavascriptInterface)
 *   - iOS     : `window.webkit.messageHandlers.kokenShare.postMessage(obj)`
 *     (KokenShareHandler, WKScriptMessageHandler)
 *
 * Les deux répondent en appelant `window.__kokenShareHandleResponse(json)`.
 *
 * Hors app native (navigateur), `isNativeShareAvailable()` renvoie false et
 * l'appelant garde son chemin web habituel : ce service ne se substitue à
 * rien, il ajoute un chemin fiable là où il existe.
 */
import { logger } from '@/services/logger';

interface PendingCall {
  resolve: (value: { shared: boolean }) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface NativeResponse {
  callbackId?: string;
  success?: boolean;
  data?: { shared?: boolean; available?: boolean };
  error?: string;
}

type AndroidBridge = { postMessage: (json: string) => void };
type IosBridge = { postMessage: (payload: unknown) => void };

/** Au-delà, on considère que le natif ne répondra pas (feuille jamais ouverte). */
const CALL_TIMEOUT_MS = 20000;

/**
 * Sur iPhone la coque ne répond qu'à la fermeture de la feuille de partage,
 * pas à son ouverture. Vingt secondes ne laissent pas le temps de choisir une
 * app et d'écrire un message : passé ce délai, un partage en cours était
 * traité comme un échec. Le délai y est donc compté large.
 */
const IOS_CALL_TIMEOUT_MS = 3 * 60 * 1000;

const pending = new Map<string, PendingCall>();
let counter = 0;
let responderInstalled = false;

function androidBridge(): AndroidBridge | null {
  const b = (window as unknown as { kokenShareAndroid?: AndroidBridge }).kokenShareAndroid;
  return b && typeof b.postMessage === 'function' ? b : null;
}

function iosBridge(): IosBridge | null {
  const handlers = (window as unknown as {
    webkit?: { messageHandlers?: Record<string, IosBridge | undefined> };
  }).webkit?.messageHandlers;
  const b = handlers?.kokenShare;
  return b && typeof b.postMessage === 'function' ? b : null;
}

/** true si l'un des deux ponts maison est présent. */
export function isNativeShareAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(androidBridge() || iosBridge());
}

/**
 * Installe le point de retour du natif. Idempotent : les deux plateformes
 * appellent la même fonction globale.
 */
function installResponder(): void {
  if (responderInstalled || typeof window === 'undefined') return;
  responderInstalled = true;
  (window as unknown as { __kokenShareHandleResponse?: (raw: string) => void })
    .__kokenShareHandleResponse = (raw: string) => {
      let parsed: NativeResponse;
      try {
        parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw as NativeResponse);
      } catch {
        logger.warn('[nativeShare] réponse native illisible');
        return;
      }
      const id = parsed.callbackId;
      if (!id) return;
      const call = pending.get(id);
      if (!call) return;
      pending.delete(id);
      clearTimeout(call.timer);
      if (parsed.success) call.resolve({ shared: Boolean(parsed.data?.shared ?? true) });
      else call.reject(new Error(parsed.error || 'native_share_failed'));
    };
}

function call(method: string, params: Record<string, unknown>): Promise<{ shared: boolean }> {
  installResponder();
  const android = androidBridge();
  const ios = iosBridge();
  if (!android && !ios) return Promise.reject(new Error('native_share_unavailable'));

  const callbackId = `share-${Date.now()}-${++counter}`;
  const body = { method, callbackId, params };

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(callbackId);
      // Un natif muet est un échec, pas un succès : c'est exactement la
      // confusion que ce service corrige.
      reject(new Error('native_share_timeout'));
    }, android ? CALL_TIMEOUT_MS : IOS_CALL_TIMEOUT_MS);
    pending.set(callbackId, { resolve, reject, timer });
    try {
      if (android) android.postMessage(JSON.stringify(body));
      else ios!.postMessage(body);
    } catch (err) {
      pending.delete(callbackId);
      clearTimeout(timer);
      reject(err instanceof Error ? err : new Error('native_share_post_failed'));
    }
  });
}

/**
 * Ouvre la feuille de partage du système avec une image PNG.
 * `dataUrl` accepte aussi bien « data:image/png;base64,… » que du base64 nu.
 */
export function shareImageNatively(options: {
  dataUrl: string;
  text?: string;
  fileName?: string;
}): Promise<{ shared: boolean }> {
  const base64 = options.dataUrl.includes(',')
    ? options.dataUrl.slice(options.dataUrl.indexOf(',') + 1)
    : options.dataUrl;
  return call('shareImage', {
    base64,
    text: options.text ?? '',
    fileName: options.fileName ?? 'koken-badge.png',
  });
}

/** Repli texte, quand l'image n'a pas pu être produite. */
export function shareTextNatively(text: string): Promise<{ shared: boolean }> {
  return call('shareText', { text });
}
